import { useQuery } from "@apollo/client";
import { useEffect } from "react";

import { GridInputs } from "../../../../../components/Functional";
import {
  FETCH_CONTINENTS,
  FETCH_COUNTRIES,
} from "../../../../../graphql/query/Country";
import { FETCH_SHIPMENT_CATEGORIES } from "../../../../../graphql/query/ShipmentCategory";
import { FETCH_VIEWING_ROOM } from "../../../../../graphql/query/ViewingRoom";
import { FETCH_WAREHOUSE_LOCATIONS } from "../../../../../graphql/query/WarehouseLocation";
import { FETCH_WAREHOUSE_LOCATION_TYPES } from "../../../../../graphql/query/WarehouseLocationType";
import { FETCH_WAREHOUSE_SITES } from "../../../../../graphql/query/WarehouseSite";

const Location = props => {
  const { formik } = props || {};
  const { data: { shipmentCategories = [] } = {} } = useQuery(
    FETCH_SHIPMENT_CATEGORIES,
  );
  const { data: { viewingRooms = [] } = {} } = useQuery(FETCH_VIEWING_ROOM);
  const { data: { warehouseSites = [] } = {} } = useQuery(
    FETCH_WAREHOUSE_SITES,
  );

  const contactName = formik.values?.deliveryContact?.name;
  const { data: { countries = [] } = {} } = useQuery(FETCH_COUNTRIES);
  const { data: { continents = [] } = {} } = useQuery(FETCH_CONTINENTS);
  const { data: { warehouseLocations = [] } = {} } = useQuery(
    FETCH_WAREHOUSE_LOCATIONS,
    {
      variables: {
        input: {
          siteId: {
            value: 0, // locations only in managed sites
            operator: "gt",
          },
        },
      },
    },
  );
  const { data: { warehouseLocationTypes = [] } = {} } = useQuery(
    FETCH_WAREHOUSE_LOCATION_TYPES,
    { variables: { input: { show: "all" } } },
  );

  useEffect(() => {
    if (formik.values?.deliveryContact?.id) {
      formik.setFieldValue(
        "_warehouseItem._warehouseMove._shipment.delContactId",
        formik.values?.deliveryContact?.id,
      );
    } else {
      formik.setFieldValue(
        "_warehouseItem._warehouseMove._shipment.delContactId",
        null,
      );
    }
  }, [formik.values?.deliveryContact?.id]);

  useEffect(() => {
    if (
      formik?.values?._warehouseItem?._warehouseMove?._shipment?.delPostcode ||
      formik?.values?._warehouseItem?._warehouseMove?._shipment
        ?._deliveryCountry?.name ||
      formik?.values?._warehouseItem?._warehouseMove?._shipment
        ?._deliveryCountry?.continent
    ) {
      formik?.setFieldValue("_warehouseItem.destroyed", {
        operator: "eq",
        value: 0,
      });
    } else {
      formik?.setFieldValue("_warehouseItem.destroyed", null);
    }
  }, [
    formik?.values?._warehouseItem?._warehouseMove?._shipment?.delPostcode,
    formik?.values?._warehouseItem?._warehouseMove?._shipment?._deliveryCountry
      ?.name,
    formik?.values?._warehouseItem?._warehouseMove?._shipment?._deliveryCountry
      ?.continent,
  ]);

  useEffect(() => {
    if (formik?.values?.checkExcludeUnlocatedShipments === -1) {
      formik.setFieldValue("excludeUnlocatedShipments", {
        value: null,
        operator: "ne",
      });
    }
  }, [formik?.values?.checkExcludeUnlocatedShipments]);

  useEffect(() => {
    if (formik?.values?.warehouseMoveLocationId) {
      formik.setFieldValue("siteManaged", null);
      formik.setFieldValue("siteId", null);
      return;
    }

    if (formik?.values?.checkSiteManaged) {
      formik.setFieldValue("siteManaged", {
        value: 0,
        operator: "gt",
      });
    } else {
      formik.setFieldValue("siteManaged", null);
    }
  }, [
    formik?.values?.checkSiteManaged,
    formik?.values?.warehouseMoveLocationId,
  ]);

  const inputs = {
    className: "grid grid-cols-1 gap-4 mt-5 pb-5",
    inputs: [
      {
        className:
          "grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6 lg:grid-cols-4 lg:gap-16",
        inputs: [
          {
            data: contactName,
            label: "Contact",
            name: "shipmentDelContactId",
            type: "contactSearch",
          },
          {
            label: "Postcode",
            name: "shipmentDelPostcode",
            type: "text",
          },
          {
            label: "Country",
            name: "shipmentDelCountryId",
            type: "multi-select",
            isMulti: false,
            options: countries?.map(item => ({
              label: item.name,
              value: item.id,
            })),
          },
          {
            label: "Continent",
            name: "shipmentDelCountryContinent",
            type: "multi-select",
            options: continents?.map(item => ({
              label: item?.continent,
              value: item?.continent,
            })),
          },
        ],
      },
      {
        className:
          "grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6 lg:grid-cols-4 lg:gap-16",
        inputs: [
          {
            label: "Viewing Room",
            name: "viewingRoomId",
            type: "multi-select",
            isMulti: false,
            options: viewingRooms?.map(item => ({
              label: item.description,
              value: item.id,
            })),
          },
          {
            label: "Date",
            name: "shipmentDate",
            type: "date",
          },
          {
            label: "Category",
            name: "shipmentCategoryId",
            type: "multi-select",
            isMulti: false,
            options: shipmentCategories?.map(item => ({
              label: item.description,
              value: item.id,
            })),
          },
          {
            label: "Sites",
            name: "siteId",
            disabled: Boolean(formik?.values?.warehouseMoveLocationId),
            type: "multi-select",
            isMulti: false,
            options: warehouseSites?.map(item => ({
              label: item.name,
              value: item.id,
            })),
            postLabelInput: {
              designType: "checkbox",
              disabled: Boolean(formik?.values?.warehouseMoveLocationId),
              label: "Managed Site",
              name: "checkSiteManaged",
              type: "toggle",
            },
          },
        ],
      },
      {
        className:
          "grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6 lg:grid-cols-4 lg:gap-16",
        inputs: [
          {
            label: "Location",
            name: "warehouseMoveLocationId",
            type: "multi-select",
            options: warehouseLocations?.map(item => ({
              label: item.description,
              value: item.id,
            })),
          },
          {
            designType: "checkbox",
            label: "Exclude Unlocated Shipments",
            name: "checkExcludeUnlocatedShipments",
            type: "toggle",
          },
        ],
      },
      {
        className:
          "grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6 lg:grid-cols-4 lg:gap-16",
        inputs: [
          {
            label: "Location Type",
            name: "warehouseLocationTypeId",
            type: "multi-select",
            isNullable: true,
            options: warehouseLocationTypes?.map(item => ({
              label: item.inactive
                ? `${item.description} (inactive)`
                : item.description,
              value: item.id,
            })),
          },
        ],
      },
    ],
  };

  const gridInputProps = {
    formik,
    ...inputs,
  };

  return (
    <div className="w-full">
      <GridInputs {...gridInputProps} />
    </div>
  );
};

export default Location;
